import { zodResolver } from '@hookform/resolvers/zod';
import {
  type Control,
  Controller,
  useForm,
  type SubmitHandler,
} from 'react-hook-form';
import { Building2, Mail, Phone, Receipt, StickyNote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  clientFormDefaults,
  clientFormSchema,
  type ClientFormValues,
} from './clientFormSchema';

interface ClientFormProps {
  defaultValues?: ClientFormValues;
  onSubmit: SubmitHandler<ClientFormValues>;
  onCancel?: () => void;
  submitLabel?: string;
  loading?: boolean;
  /**
   * Optional photo block rendered above the identity card. Create
   * flows pass a `StagedImagePicker`; the parent stages the file in
   * its own state and uploads after `createClient` returns. Photo
   * state is NOT part of the form schema — upload is a separate
   * multipart mutation.
   */
  photoSlot?: React.ReactNode;
}

/**
 * Client composer.
 *
 * Designed to feel like editing a contact card, not filling a tax
 * form. Fields are organised in two natural groups (identity +
 * contact), with tax/notes/status as a quieter trailing block. The
 * inputs sit on a soft canvas (no per-section card chrome) so the
 * eye flows top-to-bottom without "another box" cognitive load.
 */
export function ClientForm({
  defaultValues,
  onSubmit,
  onCancel,
  submitLabel = 'Save client',
  loading,
  photoSlot,
}: ClientFormProps) {
  const form = useForm<ClientFormValues>({
    resolver: zodResolver(clientFormSchema),
    defaultValues: defaultValues ?? clientFormDefaults(),
  });
  const errors = form.formState.errors;

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="flex flex-col gap-6"
      noValidate
    >
      {photoSlot}
      <article className="overflow-hidden rounded-2xl border border-line bg-card">
        <header className="flex flex-col gap-4 border-b border-line bg-secondary/30 px-6 py-6">
          <div className="flex items-start gap-4">
            <span className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-card text-muted-foreground shadow-sm">
              <Building2 className="size-5" />
            </span>
            <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
              <FieldRow
                label="Name"
                required
                error={errors.name?.message}
                htmlFor="name"
              >
                <Input
                  id="name"
                  autoComplete="organization"
                  invalid={!!errors.name}
                  className="border-transparent bg-card/80 text-base font-display font-medium shadow-sm focus-visible:bg-card"
                  placeholder="Acme Ltd."
                  {...form.register('name')}
                />
              </FieldRow>
              <FieldRow
                label="Display name"
                hint="Optional · shown in the switcher"
                error={errors.displayName?.message}
                htmlFor="displayName"
              >
                <Input
                  id="displayName"
                  maxLength={30}
                  className="border-transparent bg-card/80 shadow-sm focus-visible:bg-card"
                  placeholder="Acme"
                  {...form.register('displayName')}
                />
              </FieldRow>
              <FieldRow
                label="Company"
                hint="Optional · the legal entity, if different from above"
                error={errors.company?.message}
                htmlFor="company"
                className="sm:col-span-2"
              >
                <Input
                  id="company"
                  className="border-transparent bg-card/80 shadow-sm focus-visible:bg-card"
                  placeholder="Acme Holdings, Inc."
                  {...form.register('company')}
                />
              </FieldRow>
            </div>
          </div>
        </header>

        <div className="grid grid-cols-1 gap-3 px-6 py-5 sm:grid-cols-2">
          <IconField
            Icon={Mail}
            id="email"
            label="Email"
            error={errors.email?.message}
            inputProps={{
              type: 'email',
              autoComplete: 'email',
              placeholder: 'billing@acme.com',
              ...form.register('email'),
            }}
          />
          <IconField
            Icon={Phone}
            id="phone"
            label="Phone"
            error={errors.phone?.message}
            inputProps={{
              type: 'tel',
              autoComplete: 'tel',
              placeholder: '+1 555 123 4567',
              ...form.register('phone'),
            }}
          />
          <IconField
            Icon={Building2}
            id="website"
            label="Website"
            error={errors.website?.message}
            className="sm:col-span-2"
            inputProps={{
              type: 'url',
              placeholder: 'https://',
              ...form.register('website'),
            }}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 border-t border-line bg-secondary/20 px-6 py-5 sm:grid-cols-2">
          <IconField
            Icon={Receipt}
            id="taxId"
            label="Tax ID"
            hint="VAT, EIN, or local equivalent"
            error={errors.taxId?.message}
            inputProps={{
              placeholder: 'EIN-12-3456789',
              ...form.register('taxId'),
            }}
          />
          <SwitchRow
            control={form.control}
            name="defaultTaxExempt"
            label="Tax-exempt by default"
            description="New invoices for this client start with no tax."
          />
        </div>

        <div className="border-t border-line px-6 py-5">
          <FieldRow
            label="Internal notes"
            hint="Visible to your team only — never on the invoice"
            error={errors.notes?.message}
            htmlFor="notes"
          >
            <div className="flex items-start gap-3 rounded-xl border border-dashed border-line bg-secondary/20 px-3 py-3">
              <StickyNote className="mt-1 size-4 shrink-0 text-muted-foreground" />
              <Textarea
                id="notes"
                rows={3}
                className="resize-none border-transparent bg-transparent shadow-none focus-visible:bg-card focus-visible:shadow-sm"
                placeholder="Anything you want to remember about this client…"
                {...form.register('notes')}
              />
            </div>
          </FieldRow>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-line bg-secondary/20 px-6 py-4">
          <SwitchRow
            control={form.control}
            name="isActive"
            label="Active"
            description="Inactive clients are hidden from the invoice composer."
            inline
          />
        </div>
      </article>

      <div className="sticky bottom-0 z-10 -mx-4 flex items-center justify-end gap-2 border-t border-line bg-background/85 px-4 py-3 backdrop-blur sm:mx-0 sm:rounded-xl sm:border sm:px-4">
        {onCancel ? (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
        ) : null}
        <Button type="submit" loading={loading}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

function FieldRow({
  label,
  hint,
  required,
  error,
  htmlFor,
  className,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  error?: string;
  htmlFor?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <Label
        htmlFor={htmlFor}
        className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground"
      >
        {label}
        {required ? (
          <span aria-hidden className="ml-0.5 text-destructive">
            *
          </span>
        ) : null}
      </Label>
      {children}
      {error ? (
        <p className="text-[11px] text-destructive">{error}</p>
      ) : hint ? (
        <p className="text-[11px] text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

function IconField({
  Icon,
  id,
  label,
  hint,
  error,
  inputProps,
  className,
}: {
  Icon: typeof Mail;
  id: string;
  label: string;
  hint?: string;
  error?: string;
  inputProps: React.InputHTMLAttributes<HTMLInputElement> & {
    name?: string;
    onBlur?: React.FocusEventHandler<HTMLInputElement>;
    onChange?: React.ChangeEventHandler<HTMLInputElement>;
  };
  className?: string;
}) {
  return (
    <div className={className}>
      <Label
        htmlFor={id}
        className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground"
      >
        {label}
      </Label>
      <div
        className={cn(
          'mt-1 flex items-center gap-2 rounded-lg border bg-card px-3 transition-colors',
          error
            ? 'border-destructive'
            : 'border-line focus-within:border-line-strong',
        )}
      >
        <Icon className="size-4 shrink-0 text-muted-foreground" />
        <Input
          id={id}
          {...inputProps}
          className="h-10 border-transparent bg-transparent px-0 shadow-none focus-visible:ring-0"
        />
      </div>
      {error ? (
        <p className="mt-1 text-[11px] text-destructive">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

function SwitchRow({
  control,
  name,
  label,
  description,
  inline,
}: {
  control: Control<ClientFormValues>;
  name: 'defaultTaxExempt' | 'isActive';
  label: string;
  description?: string;
  inline?: boolean;
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <label
          className={cn(
            'flex cursor-pointer items-start justify-between gap-4',
            inline ? 'flex-1' : 'rounded-xl border border-dashed border-line bg-secondary/20 px-4 py-3',
          )}
        >
          <div>
            <div className="text-sm font-medium text-foreground">{label}</div>
            {description ? (
              <div className="mt-0.5 text-[11px] text-muted-foreground">
                {description}
              </div>
            ) : null}
          </div>
          <Switch
            checked={field.value}
            onCheckedChange={field.onChange}
            aria-label={label}
          />
        </label>
      )}
    />
  );
}
